"use client";

import { useEffect, useState } from "react";
import {
  Banner,
  BannerAction,
  BannerIcon,
  BannerTitle,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from "@gogive/design-system";
import { CreditCard, ExternalLink } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useAccount } from "@/hooks/useAccount";
import { useStripeConnectLink } from "@/hooks/useStripeConnectLink";
import {
  isStripeReturnCallback,
  stripeConnectCallbackUrls,
} from "@/lib/stripe-return";

const SESSION_FLAG = "gogive.stripeConnectModalShown";

export function StripeConnectTopBarAlert() {
  const { account, isLoading: accountLoading } = useAccount();
  const needsConnect = !accountLoading && !!account && !account.stripeConnected;
  const { stripeConnectLink, isLoading: linkLoading } = useStripeConnectLink(
    needsConnect,
    stripeConnectCallbackUrls
  );

  if (!needsConnect || linkLoading || !stripeConnectLink?.url) return null;

  return (
    <Banner inset className="max-w-full shadow-none">
      <div className="flex min-w-0 items-center gap-2">
        <BannerIcon icon={CreditCard} className="hidden shrink-0 sm:flex" />
        <BannerTitle className="min-w-0 truncate">
          <span className="md:hidden">Connect Stripe</span>
          <span className="hidden md:inline">
            Connect Stripe to accept donations
          </span>
        </BannerTitle>
      </div>
      <BannerAction
        asChild
        className="border-0 bg-primary-foreground text-primary shadow-none hover:bg-primary-foreground/90 data-[hover]:bg-primary-foreground/90"
      >
        <a href={stripeConnectLink.url} rel="noreferrer">
          Connect
        </a>
      </BannerAction>
    </Banner>
  );
}

export function StripeConnectNudge() {
  const searchParams = useSearchParams();
  const { account, isLoading: accountLoading } = useAccount();
  const needsConnect = !accountLoading && !!account && !account.stripeConnected;
  const { stripeConnectLink, isLoading: linkLoading } = useStripeConnectLink(
    needsConnect,
    stripeConnectCallbackUrls
  );

  const [modalOpen, setModalOpen] = useState(false);
  const showBanner =
    needsConnect && !linkLoading && !!stripeConnectLink?.url;

  useEffect(() => {
    if (!showBanner) return;

    try {
      if (sessionStorage.getItem(SESSION_FLAG) !== "1") {
        sessionStorage.setItem(SESSION_FLAG, "1");
        setModalOpen(true);
      }
    } catch {
      setModalOpen(true);
    }
  }, [showBanner]);

  if (isStripeReturnCallback(searchParams)) return null;
  if (!needsConnect) return null;

  return (
    <>
      <AlertDialog open={modalOpen} onOpenChange={setModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Connect Stripe to accept donations</AlertDialogTitle>
            <AlertDialogDescription>
              Your charity is set up, but donors can’t pay until Stripe is
              connected. You can do this now or later from any page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Later</AlertDialogCancel>
            <Button
              asChild={!!stripeConnectLink?.url}
              disabled={linkLoading || !stripeConnectLink?.url}
            >
              {stripeConnectLink?.url ? (
                <a href={stripeConnectLink.url} rel="noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Connect Stripe
                </a>
              ) : (
                <span>{linkLoading ? "Preparing link…" : "Connect Stripe"}</span>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
